/** 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * GLOBAL UI — tek kaynak qubepro-core; yalnız resmî UI senkronuyla dağıtılır.
 */
import { forwardRef, useLayoutEffect, useRef, type ButtonHTMLAttributes, type HTMLAttributes } from 'react';
import { Icon } from './ComponentIcon';
import { registerModalFocusReturnTarget } from './modal-focus-return.utils';
import { getTableContentPaintRects, paintRectsOverlap, revealRowActionsSpace } from './table-row-actions-layout.utils';
import './DataTable.css';

export type TableRowActionButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className' | 'style' | 'title'> & {
    title: string;
    icon: string;
    variant?: 'view' | 'edit' | 'copy' | 'delete' | 'preview' | 'test';
};

/** Core owns the icon and button presentation; callers own labels, permissions and actions. */
export const TableRowActionButton = forwardRef<HTMLButtonElement, TableRowActionButtonProps>(
    function TableRowActionButton({ title, icon, variant = 'view', onClick, ...props }, ref) {
        return <button {...props} ref={ref} type="button" title={title}
            aria-label={props['aria-label'] || title} className={`qp-row-action btn-${variant}`}
            onClick={(event) => { event.stopPropagation(); onClick?.(event); }}>
            <Icon name={icon} size={18} />
        </button>;
    },
);

/** Shared floating action surface; the host owns authorization and callbacks. */
export const TableRowActionsSurface = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & {
    protectedRow?: HTMLTableRowElement | null;
    reservedSpace?: number;
    onSpaceRequirementChange?: (height: number) => void;
}>(
    function TableRowActionsSurface({ className, protectedRow, reservedSpace = 0, onSpaceRequirementChange, style, children, ...props }, ref) {
        const surface = useRef<HTMLDivElement | null>(null);
        useLayoutEffect(() => {
            if (surface.current && protectedRow) {
                return registerModalFocusReturnTarget(surface.current, protectedRow);
            }
            return undefined;
        }, [protectedRow]);
        useLayoutEffect(() => {
            const node = surface.current;
            const viewport = protectedRow?.closest('.datatable-table-viewport');
            if (!node || !protectedRow || !viewport) return;
            const update = () => {
                const bounds = viewport.getBoundingClientRect();
                const right = Math.min(innerWidth - 4, bounds.right - 6);
                const left = Math.max(4, bounds.left + 4);
                node.style.right = `${Math.max(4, innerWidth - right)}px`;
                node.style.maxWidth = `${Math.max(0, right - left)}px`;
                const row = protectedRow.getBoundingClientRect();
                const content = protectedRow.querySelector<HTMLElement>(
                    ':scope > td:not(.selection-column):not(.actions-col) > :first-child',
                )?.getBoundingClientRect();
                const centre = content && content.height > 0
                    ? Math.max(row.top, Math.min(row.bottom, content.top + content.height / 2))
                    : row.top + Math.min(row.height / 2, 22);
                const protectedPaint = getTableContentPaintRects(viewport);
                const spaceRequirement = () => {
                    const css = getComputedStyle(node);
                    const gap = (Number.parseFloat(css.paddingTop) || 0) + (Number.parseFloat(css.borderTopWidth) || 0);
                    return Math.ceil(node.getBoundingClientRect().height + 2 * gap);
                };
                const place = (requestedTop: number) => {
                    const height = node.getBoundingClientRect().height;
                    const top = Math.max(height / 2 + 4, Math.min(requestedTop, innerHeight - height / 2 - 4));
                    node.style.top = `${top}px`;
                    const surface = node.getBoundingClientRect();
                    return protectedPaint.some(target => paintRectsOverlap(surface, target));
                };
                // Keep the table full width. Compact the floating actions only
                // when there is no horizontal space beside the row controls.
                node.classList.remove('datatable-row-actions-overlay--compact');
                const overflow = node.scrollWidth > node.clientWidth;
                const space = protectedRow.nextElementSibling;
                if (reservedSpace > 0 && space?.hasAttribute('data-row-actions-space')) {
                    if (overflow) node.classList.add('datatable-row-actions-overlay--compact');
                    const height = spaceRequirement();
                    onSpaceRequirementChange?.(height);
                    revealRowActionsSpace(space);
                    const lane = space.getBoundingClientRect();
                    node.style.top = `${lane.top + lane.height / 2}px`;
                    return;
                }
                if (!overflow && !place(centre)) return;
                if (!overflow) {
                    const railWidth = node.getBoundingClientRect().width;
                    const gap = 6;
                    const horizontalPositions = protectedPaint.flatMap(target => {
                        return [target.left - railWidth - gap, target.right + gap];
                    }).filter(x => x >= left && x + railWidth <= right).sort((a, b) => b - a);
                    for (const x of horizontalPositions) {
                        node.style.right = `${innerWidth - x - railWidth}px`;
                        if (!place(centre)) return;
                    }
                }
                // No free paint lane: reserve space only for the open row, not a
                // permanent action column. Keep horizontal controls whenever they fit.
                if (onSpaceRequirementChange) {
                    if (overflow) node.classList.add('datatable-row-actions-overlay--compact');
                    onSpaceRequirementChange(spaceRequirement());
                    return;
                }
                node.style.right = `${Math.max(4, innerWidth - right)}px`;
                node.classList.add('datatable-row-actions-overlay--compact');
                if (!place(centre)) return;
                const height = node.getBoundingClientRect().height;
                for (const candidate of [row.bottom + height / 2 + 4, row.top - height / 2 - 4]) {
                    if (candidate - height / 2 < Math.max(4, bounds.top)
                        || candidate + height / 2 > Math.min(innerHeight - 4, bounds.bottom)) continue;
                    if (!place(candidate)) return;
                }
                place(centre);
            };
            update();
            const observer = new ResizeObserver(update);
            observer.observe(viewport);
            return () => observer.disconnect();
        }, [protectedRow, reservedSpace, onSpaceRequirementChange, style?.top, style?.right, style?.maxWidth, children]);
        return <div {...props} style={style} ref={node => { surface.current = node; if (typeof ref === 'function') ref(node); else if (ref) ref.current = node; }} role="toolbar" data-no-row-click="true"
            className={['datatable-row-actions-overlay', className].filter(Boolean).join(' ')}>{children}</div>;
    },
);
